import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';

import history, { type Path } from 'Core/history';
import type { FacetValueBase, SelectionEntry } from 'Models/index';
import requestConfig from 'Models/uiConfig/requestConfig';
import type { RequestExtra } from 'Models/uiConfig/requestTypes';
import type { VehicleFieldMap } from 'Models/vehicle';
import { getRequestFromUri } from 'Modules/converter/index';
import { abortControllerCreator, search } from 'Modules/serverApi/index';
import type { ServerModel } from 'Modules/serverApi/types';

type NormRequest = Partial<VehicleFieldMap>;

export type NormalizedEntry = {
  value: string;
  term: string;
  selected: boolean;
};

export type NormResponse = {
  field: string;
  title: string;
  entries: NormalizedEntry[];
}[];

export type UseAutonomousSearchResult = {
  isLoading: boolean;
  isResponsePending: boolean;
  response: NormResponse;
  clear: () => void;
  onChange: (field: string) => (value: string) => void;
};

export default function useAutonomousSearch(
  fields: string[],
  requestExtra?: RequestExtra,
  storedSelection?: NormRequest | null,
): UseAutonomousSearchResult {
  const createAbortController = useMemo(() => abortControllerCreator(), []);
  const [isLoading, setIsLoading] = useState(true);
  const [isResponsePending, setIsResponsePending] = useState(false);
  const [facets, setFacets] = useState<ServerModel.FacetCategory[]>();
  const [selection, setSelection] = useState<NormRequest | null | undefined>(storedSelection);
  const [currentLocation, setCurrentLocation] = useState<Path>(window.location);
  const isInitialized = useMemo(() => !!storedSelection, [storedSelection]);

  useEffect(
    function sendRequest() {
      if (!selection) {
        return;
      }

      search(
        {
          selection: [...denormalizeRequest(selection)],
          extra: { mode: 'RequestPanel', ...(requestExtra || {}) },
          pageSize: 0,
        },
        createAbortController(),
      ).then((response) => {
        if (response.State === 'cancelled') {
          return;
        }
        setIsLoading(false);
        setIsResponsePending(false);
        setFacets(requestConfig.responseHandler(response).Facets);
        setSelection((prev) => {
          if (!prev || Object.entries(prev).length === 0) {
            const next = buildSelectionFromFacets(requestConfig.responseHandler(response).Facets, fields);
            return Object.keys(next).length ? next : prev;
          }
          return prev;
        });
      });
    },
    [createAbortController, fields, requestExtra, selection],
  );

  useEffect(function subscribeToHistory() {
    return history.listen(({ location }) => setCurrentLocation(location));
  }, []);

  useEffect(
    function updateSelectionOnFieldsOrHistoryChange() {
      if (isInitialized) {
        return;
      }

      setSelection((prevSelection) => {
        const nextSelection = normalizeRequest(fields, getRequestFromUri(currentLocation).selection);
        return prevSelection && selectionsEqual(prevSelection, nextSelection) ? prevSelection : nextSelection;
      });
    },
    [currentLocation, fields, isInitialized],
  );

  const clear = useCallback(() => {
    setIsLoading(true);
    setSelection((selection) =>
      selection && Object.entries(selection).length ? {} : { ...(selection ?? {}) },
    );
  }, []);

  const onChange = useCallback(
    (field: string) => (value: string) => {
      if (selection?.[field] === value) {
        return;
      }

      setIsResponsePending(true);
      setSelection({ ...selection, [field]: value || undefined });
    },
    [selection],
  );

  return {
    isLoading,
    isResponsePending,
    response: useMemo(() => normalizeResponse(fields, facets), [facets, fields]),
    clear,
    onChange,
  };
}

function buildSelectionFromFacets(facets: ServerModel.FacetCategory[], fields: string[]): NormRequest {
  return Object.fromEntries(
    facets
      .map((f) => {
        const selected = f.Values?.find((v) => v.Selected);
        return selected?.Term && fields.includes(f.FieldName) ? [f.FieldName, selected.Term] : null;
      })
      .filter((entry): entry is [string, string] => entry !== null),
  );
}

function normalizeRequest(
  fields: string[],
  selection: Array<Pick<SelectionEntry, 'field' | 'term'>>,
): NormRequest {
  return Object.fromEntries(
    fields.map((field) => [field, selection.find((v) => v.field === field)?.term]).filter((pair) => pair[1]),
  );
}

function denormalizeRequest(selection: NormRequest): FacetValueBase[] {
  return Object.entries(selection)
    .map(([field, term]) => ({ field, term }))
    .filter((v): v is FacetValueBase => Boolean(v.term));
}

function normalizeResponse(fields: string[], facets: ServerModel.FacetCategory[] | undefined): NormResponse {
  return facets
    ? fields.flatMap((field) => {
        const facet = facets.find((candidate) => candidate.FieldName === field);
        return facet
          ? [
              {
                field,
                title: facet.DisplayName || field,
                entries: (facet.Values ?? []).map((v) => ({
                  value: v.Value ?? '',
                  term: v.Term ?? v.Value ?? '',
                  selected: v.Selected ?? false,
                })),
              },
            ]
          : [];
      })
    : [];
}

function selectionsEqual(prevSelection: NormRequest, nextSelection: NormRequest): boolean {
  const prevFields = Object.keys(prevSelection);
  const nextFields = Object.keys(nextSelection);
  return (
    prevFields.length === nextFields.length &&
    [...prevFields, ...nextFields].every((field) => prevSelection[field] === nextSelection[field])
  );
}
